import { Inject, Injectable } from '@angular/core';
import { ITealiumAnalyticsOptions, TealiumAnalyticsEvent, TealiumAnalyticsService } from '@ddt-web/analytics';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { PasPortfolioPosition } from '@ddt-web/pas';
import { StoreService } from '@ddt-web/store-service';
import { filter, take } from 'rxjs/operators';

import { PasPostProcessingService } from './pas-post-processing.service';

/**
 * Service that tracks blk fund suggestions per analysis in IQ. To be triggered after a blk ideas security call.
 */
@Injectable()
export class PasBlkIdeasAnalyticsProcessingService extends PasPostProcessingService {

	constructor(
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) {
		super();
	}
	/**
	 * Matches the fund portfolios with the blk ideas returned by security call. Id decides which entity to get from the store.
	 * It gets either general fund ideas, or buy funds fund ideas. It then sends a tracking event.
	 * @param portfoliosToLoad this is expected to be the blk ideas securities.
	 */
	public process(portfoliosToLoad: PasPortfolioPosition[]): void {

		const blkIdeasIdToPortfolioFundsId = {
			'transient-blk-ideas': 'transient-book',
			'transient-buy-funds-blk-ideas': 'transient-buy-funds'
		};

		const idForPortfolioFunds: string = blkIdeasIdToPortfolioFundsId[portfoliosToLoad?.[0]?.id];

		this.storeService.selectById(this.storeConfig.positionDetails, idForPortfolioFunds)
			.pipe(
				filter(positionDetails => !!positionDetails),
				take(1)
			)
			.subscribe(positionDetails => {
				let eventStringAllRecommendedFunds = '';
				let eventStringNotInPortfolioRecommendedFunds = '';

				portfoliosToLoad?.[0]?.children?.forEach(fundSuggestion => {
					const isBlkIdeaNotInPortolios = !positionDetails?.[idForPortfolioFunds]?.children?.some(portfoliosFund =>
						portfoliosFund.ticker === fundSuggestion.ticker
					);
					eventStringAllRecommendedFunds += `${fundSuggestion.ticker}:`;
					if (isBlkIdeaNotInPortolios) {
						eventStringNotInPortfolioRecommendedFunds += `${fundSuggestion.ticker}:`;
					}
				});

				if (eventStringAllRecommendedFunds) {
					const allRecommendedOptions: ITealiumAnalyticsOptions = { fteMap: eventStringAllRecommendedFunds.slice(0, -1) };
					this.tealiumAnalyticsService.trackEventTealium(
						new TealiumAnalyticsEvent(`iq-analysis:${portfoliosToLoad?.[0]?.id}:all-recommended-funds`, allRecommendedOptions)
					);
				}

				if (eventStringNotInPortfolioRecommendedFunds) {
					const notInPortfolioRecommendedOptions: ITealiumAnalyticsOptions = { fteMap: eventStringNotInPortfolioRecommendedFunds.slice(0, -1) };
					this.tealiumAnalyticsService.trackEventTealium(
						new TealiumAnalyticsEvent(`iq-analysis:${portfoliosToLoad?.[0]?.id}:not-in-portfolio-recommended-funds`, notInPortfolioRecommendedOptions)
					);
				}
			});
	}
}
